import React, { memo, useId } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { BaseItem, TableProps } from '@lib/components/Table/types';
import { getColumnsCount } from '@lib/components/Table/utils';
import Typography from '@lib/components/Typography/Typography';
import TableBodyRow from '@lib/components/Table/components/TableBody/TableBodyRow';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import styles from './TableBody.module.scss';

type Props = {
  hasActionsColumn: boolean;
  hasActionsMenuColumn: boolean;
  hasSelectColumn: boolean;
  isSelectionLimitReached?: boolean;
};

function TableBody<T extends BaseItem>(props: Props & TableProps<T>) {
  const {
    actions,
    actionsMenu,
    borderedCells,
    columns,
    data,
    getIsRowDisabled,
    getIsRowSelected = () => false,
    getRowClassName,
    hasActionsColumn,
    hasActionsMenuColumn,
    hasSelectColumn,
    isSelectionLimitReached,
    onRowClick,
    radioSelect,
    selectionLimit,
    tree,
    useBulkSelectResult = {} as UseBulkSelectResult<T>,
  } = props;
  const { t } = useTranslation();
  // One name for every row radio, so the browser and assistive technology see
  // a single group — and a different one per table instance on the page.
  const radioGroupName = `table-body-radio-${useId()}`;
  const className = classNames(styles.body, {
    [styles.tableWithBorderedCells]: borderedCells,
  });
  return (
    <tbody className={className}>
      {data.length ? (
        data.map((item, index) => (
          <TableBodyRow
            actions={actions}
            actionsMenu={actionsMenu}
            columns={columns}
            data={data}
            getIsRowDisabled={getIsRowDisabled}
            getIsRowSelected={getIsRowSelected}
            getRowClassName={getRowClassName}
            hasActionsColumn={hasActionsColumn}
            hasActionsMenuColumn={hasActionsMenuColumn}
            hasSelectColumn={hasSelectColumn}
            isSelectionLimitReached={isSelectionLimitReached}
            key={item?.id || uuidv4()}
            onRowClick={onRowClick}
            radioGroupName={radioGroupName}
            radioSelect={radioSelect}
            rowIndex={index}
            rowItem={item}
            selectionLimit={selectionLimit}
            tree={tree}
            useBulkSelectResult={useBulkSelectResult}
          />
        ))
      ) : (
        <tr>
          <td colSpan={getColumnsCount(props)}>
            <div className={styles.tdContent}>
              <Typography variant="body">{t('no-results')}</Typography>
            </div>
          </td>
        </tr>
      )}
    </tbody>
  );
}

export default memo(TableBody) as <T extends BaseItem>(
  props: Props & TableProps<T>,
) => React.ReactElement;
